import { css, IconButton, IconButtonProps, styled } from "@mui/material";
import React, { ReactNode, SVGProps } from "react";

import { TOOLBAR_BUTTON, TOOLBAR_COLORS } from "components/toolbars/constants";

interface DefaultIconButtonProps extends IconButtonProps {
    active?: boolean;
    hasExpander?: boolean;
    expanderPosition?: "bottom-left" | "bottom-right";
    children: ReactNode;
}

const StyledIconButton = styled(IconButton, {
    shouldForwardProp: (prop) => !["active", "hasExpander", "expanderPosition"].includes(prop as string),
})<{ active?: boolean }>(
    ({ active }) => css`
        background-color: ${active ? TOOLBAR_COLORS.ACTIVE_BG : "transparent"};
        color: ${active ? TOOLBAR_COLORS.ACTIVE : TOOLBAR_COLORS.DEFAULT};
        border-radius: ${TOOLBAR_BUTTON.RADIUS}px;
        width: ${TOOLBAR_BUTTON.SIZE}px;
        height: ${TOOLBAR_BUTTON.SIZE}px;
        padding: ${TOOLBAR_BUTTON.PADDING}px;
        position: relative;
        &:hover {
            background-color: ${active ? TOOLBAR_COLORS.ACTIVE_HOVER : TOOLBAR_COLORS.HOVER};
        }
    `,
);

/**
 * Icon button with optional expander indicator for menu buttons
 */
export const DefaultIconButton = React.forwardRef<HTMLButtonElement, DefaultIconButtonProps>(
    ({ id, active, hasExpander, expanderPosition, disabled, children, ...props }, ref) => {
        return (
            <StyledIconButton
                data-testid={`default-icon-button-${id}`}
                ref={ref}
                active={active}
                aria-pressed={active}
                disabled={disabled}
                {...props}
            >
                {children}
                {hasExpander && !disabled && <ExpanderIcon position={expanderPosition} />}
            </StyledIconButton>
        );
    },
);

function ExpanderIcon({ position = "bottom-right" }: { position?: "bottom-left" | "bottom-right" }) {
    return (
        <ExpanderIconSvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 7 7" position={position}>
            <path
                d="M5.98565 6.98585L1.34325 6.98583C0.452344 6.98583 0.0061835 5.90868 0.636151 5.27872L5.27856 0.63635C5.90852 0.0063889 6.98566 0.452557 6.98566 1.34346L6.98566 5.98585C6.98566 6.53813 6.53794 6.98585 5.98565 6.98585Z"
                fill="currentColor"
            />
        </ExpanderIconSvg>
    );
}

const ExpanderIconSvg = styled("svg", { shouldForwardProp: (prop) => prop !== "position" })<
    SVGProps<SVGSVGElement> & { position?: "bottom-left" | "bottom-right" }
>(
    ({ position = "bottom-right" }) => css`
        width: 7px;
        height: 7px;
        color: ${TOOLBAR_COLORS.EXPANDER_DEFAULT};
        position: absolute;
        bottom: 2px;
        ${position === "bottom-left"
            ? css`
                  left: 2px;
                  transform: scaleX(-1);
              `
            : css`
                  right: 2px;
              `}
    `,
);
